// eslint-disable-next-line no-unused-vars
import React, { useCallback } from 'react';
import firebase from 'gatsby-plugin-firebase';
import moment from 'moment';
import useErrorNotifier from './use-error-notifier';
import {
	CONVERSATION_COULD_NOT_BE_CREATED,
	CONVERSATION_MESSAGE_COULD_NOT_BE_SENT
} from '../utils/errorCodes';
import { getCurrentDateAsJson } from '../utils/formatter';

const useConversation = () => {
	const { setErrorFunction } = useErrorNotifier();
	const conversationRef = firebase.database().ref('conversation');
	const mentionsRef = firebase.database().ref('message_mention');
	const messagesRef = firebase.database().ref('message');

	const conversationParticipantRef = firebase
		.database()
		.ref('conversation_participant');
	const participantUnreadMessagesRef = firebase
		.database()
		.ref('participant_unread_messages');

	const findConversationByName = async (name, isRefund = false) => {
		let conversation = null;
		await conversationRef
			.orderByChild('name')
			.equalTo(name)
			.once('value', (convSnapshot) => {
				convSnapshot.forEach((convChildSnapshot) => {
					const foundConversation = convChildSnapshot.val();
					foundConversation.id = convChildSnapshot.key;
					if (foundConversation.refund == isRefund) {
						conversation = foundConversation;
					}
				});
			});

		return conversation;
	};

	const findConversationByChild = async (
		child = '',
		value,
		liveEvent = false
	) => {
		let conversation = null;
		await conversationRef
			.orderByChild(child)
			.equalTo(value)
			.once('value', (convSnapshot) => {
				convSnapshot.forEach((convChildSnapshot) => {
					const foundConversation = convChildSnapshot.val();
					foundConversation.id = convChildSnapshot.key;
					if (liveEvent) {
						if (foundConversation.live_event) {
							conversation = foundConversation;
						}
					} else {
						conversation = foundConversation;
					}
				});
			});

		return conversation;
	};

	const findConversationByKey = async (key) => {
		let conversation = null;
		await conversationRef.child(key).once('value', (convSnapshot) => {
			convSnapshot.forEach((convChildSnapshot) => {
				const foundConversation = convChildSnapshot.val();
				foundConversation.id = convChildSnapshot.key;
				conversation = foundConversation;
			});
		});

		return conversation;
	};

	const addParticipantToConversation = async (
		conversationId,
		participantUserId = '',
		showConversation = true,
		additionalObj = null
	) => {
		let existingParticipant = null;
		await conversationParticipantRef
			.orderByChild('conversation_id')
			.equalTo(conversationId)
			.once('value', (convSnapshot) => {
				convSnapshot.forEach((convChildSnapshot) => {
					const foundParticipant = convChildSnapshot.val();
					if (foundParticipant.user_id == participantUserId) {
						foundParticipant.id = convChildSnapshot.key;
						existingParticipant = foundParticipant;
					}
				});
			});

		if (!existingParticipant) {
			let participantObj = {
				conversation_id: conversationId,
				user_id: participantUserId,
				date: getCurrentDateAsJson(),
				last_seen_date: moment().subtract(1, 'minutes').utc().toDate()
					.toJSON(),
				show_conversation: showConversation
			};
			if (additionalObj) {
				participantObj = { ...participantObj, ...additionalObj };
			}
			return conversationParticipantRef.push(participantObj);
		}
		return null;
	};

	const increaseParticipantUnreadMessages = useCallback(
		(conversationId, userId) => {
			firebase
				.database()
				.ref('participant_unread_messages')
				.orderByChild('user_id')
				.equalTo(userId)
				.once('value', (snapshot) => {
					let tempConversationsUnreadMessages = [];
					if (snapshot.val() && Object.keys(snapshot.val())[0]) {
						const key = Object.keys(snapshot.val()).toString();
						const participantUnreadMessage = snapshot.val()[
							Object.keys(snapshot.val())[0]
						];

						tempConversationsUnreadMessages =							participantUnreadMessage.conversation_unread_messages;

						const conversation = tempConversationsUnreadMessages.find(
							(c) => c.conversation_id == conversationId
						);

						if (conversation) {
							tempConversationsUnreadMessages = tempConversationsUnreadMessages.map(
								(c) => {
									if (c.conversation_id == conversationId) {
										c.number_of_unread_messages += 1;
										c.last_date_updated = getCurrentDateAsJson();
									}

									return c;
								}
							);
						} else {
							tempConversationsUnreadMessages.push({
								conversation_id: conversationId,
								number_of_unread_messages: 1,
								last_date_updated: getCurrentDateAsJson()
							});
						}

						firebase
							.database()
							.ref(`participant_unread_messages/${key}`)
							.update({
								number_of_unread_messages:
									participantUnreadMessage.number_of_unread_messages + 1,
								conversation_unread_messages: tempConversationsUnreadMessages
							});
					} else {
						tempConversationsUnreadMessages.push({
							conversation_id: conversationId,
							number_of_unread_messages: 1,
							last_date_updated: getCurrentDateAsJson()
						});
						participantUnreadMessagesRef.push({
							user_id: userId,
							number_of_unread_messages: 1,
							conversation_unread_messages: tempConversationsUnreadMessages
						});
					}
				});
		},
		[]
	);

	const clearParticipantUnreadMessages = useCallback(
		(conversationId, userId) => {
			firebase
				.database()
				.ref('participant_unread_messages')
				.orderByChild('user_id')
				.equalTo(userId)
				.once('value', (snapshot) => {
					if (snapshot.val() && Object.keys(snapshot.val())[0]) {
						let numberOfUnreadMessages = 0;
						let tempConversationsUnreadMessages = [];
						const key = Object.keys(snapshot.val()).toString();
						const participantUnreadMessage = snapshot.val()[
							Object.keys(snapshot.val())[0]
						];
						tempConversationsUnreadMessages =							participantUnreadMessage.conversation_unread_messages;

						const conversation = tempConversationsUnreadMessages.find(
							(c) => c.conversation_id == conversationId
						);

						if (conversation) {
							tempConversationsUnreadMessages = tempConversationsUnreadMessages.map(
								(c) => {
									if (c.conversation_id == conversationId) {
										c.number_of_unread_messages = 0;
										c.last_date_updated = getCurrentDateAsJson();
									}
									numberOfUnreadMessages += c.number_of_unread_messages;

									return c;
								}
							);
						} else {
							tempConversationsUnreadMessages.push({
								conversation_id: conversationId,
								number_of_unread_messages: 0,
								last_date_updated: getCurrentDateAsJson()
							});
						}
						firebase
							.database()
							.ref(`participant_unread_messages/${key}`)
							.update({
								number_of_unread_messages: numberOfUnreadMessages,
								conversation_unread_messages: tempConversationsUnreadMessages
							});
					}
				});
		},
		[]
	);

	const initializeParticipantUnreadMessage = useCallback(
		(
			userId,
			conversationId,
			isHost = false,
			hostShouldGetNotificationAsWell = false
		) => {
			firebase
				.database()
				.ref('participant_unread_messages')
				.orderByChild('user_id')
				.equalTo(userId)
				.once('value', (snapshot) => {
					let tempConversationsUnreadMessages = [];
					if (snapshot.val() && Object.keys(snapshot.val())[0]) {
						const participantUnreadMessage = snapshot.val()[
							Object.keys(snapshot.val())[0]
						];
						const key = Object.keys(snapshot.val()).toString();
						tempConversationsUnreadMessages =							participantUnreadMessage.conversation_unread_messages;
						tempConversationsUnreadMessages.push({
							conversation_id: conversationId,
							number_of_unread_messages:
								isHost && !hostShouldGetNotificationAsWell ? 0 : 1,
							last_date_updated: getCurrentDateAsJson()
						});

						const unreadMessagesObj = {
							conversation_unread_messages: tempConversationsUnreadMessages
						};
						if (
							(!isHost || hostShouldGetNotificationAsWell)
							&& participantUnreadMessage
							&& participantUnreadMessage.number_of_unread_messages !== undefined
						) {
							unreadMessagesObj.number_of_unread_messages =								participantUnreadMessage.number_of_unread_messages + 1;
						}

						firebase
							.database()
							.ref(`participant_unread_messages/${key}`)
							.update(unreadMessagesObj);
					} else {
						tempConversationsUnreadMessages.push({
							conversation_id: conversationId,
							number_of_unread_messages:
								isHost && !hostShouldGetNotificationAsWell ? 0 : 1,
							last_date_updated: getCurrentDateAsJson()
						});
						participantUnreadMessagesRef.push({
							user_id: userId,
							number_of_unread_messages:
								isHost && !hostShouldGetNotificationAsWell ? 0 : 1,
							conversation_unread_messages: tempConversationsUnreadMessages
						});
					}
				});
		},
		[]
	);

	const createConversation = async (
		name = '',
		hostUserId = '',
		isGroup = true,
		allowAddParticipants = true,
		additionalObj = null,
		showConversation = true,
		participantUserIds = [],
		hostMessageShouldGetChatNotificationAsWell = false
	) => {
		let conversationObj = {
			name,
			date: getCurrentDateAsJson(),
			user_id: hostUserId,
			last_seen_date: moment().subtract(1, 'minutes').utc().toDate()
				.toJSON(),
			show_conversation: showConversation,
			active: true,
			is_group: isGroup
		};
		if (additionalObj) {
			conversationObj = { ...conversationObj, ...additionalObj };
		}

		if (!allowAddParticipants) {
			conversationObj.disable_add_participants = false;
		}

		let createdConversationSnapshot = null;
		try {
			createdConversationSnapshot = await conversationRef.push(conversationObj);
			if (!additionalObj || !additionalObj.live_event) {
				initializeParticipantUnreadMessage(
					hostUserId,
					createdConversationSnapshot.key,
					true,
					hostMessageShouldGetChatNotificationAsWell
				);
			}

			if (participantUserIds && participantUserIds.length) {
				participantUserIds.forEach((userId) => {
					conversationParticipantRef.push({
						conversation_id: createdConversationSnapshot.key,
						user_id: userId,
						date: getCurrentDateAsJson(),
						last_seen_date: moment()
							.subtract(1, 'minutes')
							.utc()
							.toDate()
							.toJSON(),
						show_conversation: true
					});

					if (!additionalObj || !additionalObj.live_event) {
						initializeParticipantUnreadMessage(
							userId,
							createdConversationSnapshot.key
						);
					}
				});
			}
		} catch (err) {
			setErrorFunction(CONVERSATION_COULD_NOT_BE_CREATED);
		}
		return createdConversationSnapshot.key;
	};

	const sendMessage = (message, conversationId, userId, mentions = []) => {
		messagesRef
			.push({
				conversation_id: conversationId,
				user_id: userId,
				sent_date: getCurrentDateAsJson(),
				message
			})
			.then((snap) => {
				if (mentions && mentions.length > 0) {
					const messageId = snap.key;
					mentions.forEach((mention) => {
						mentionsRef.push({
							messageId,
							conversation_id: conversationId,
							user_mentioned: mention,
							seen: false
						});
					});
				}
			})
			.catch(() => {
				setErrorFunction(CONVERSATION_MESSAGE_COULD_NOT_BE_SENT);
			});
	};

	const getMessages = async (conversationId) => {
		const messages = [];
		if (!conversationId) return [];
		await messagesRef
			.orderByChild('conversation_id')
			.equalTo(conversationId)
			.once('value', (snapshot) => {
				snapshot.forEach((childSnapshot) => {
					const message = childSnapshot.val();
					message.id = childSnapshot.key;
					messages.push(message);
				});
			});

		return messages;
	};

	const getConversationParticipants = async (conversationId) => {
		const participants = [];
		if (!conversationId) return participants;
		await conversationParticipantRef
			.orderByChild('conversation_id')
			.equalTo(conversationId)
			.once('value', (snapshot) => {
				snapshot.forEach((snap) => {
					const participant = snap.val();
					participant.id = snap.key;
					participants.push(participant);
				});
			});

		return participants;
	};
	return {
		createConversation,
		addParticipantToConversation,
		findConversationByChild,
		findConversationByKey,
		findConversationByName,
		getMessages,
		sendMessage,
		increaseParticipantUnreadMessages,
		clearParticipantUnreadMessages,
		getConversationParticipants
	};
};

export default useConversation;
